import React, { useEffect, useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getWithTokenAPI } from "../../util/Api";
import { students_list_approved, students_list_autoapproved } from "../../util/Apiendpoits";
import { SafeToast } from "../Toast/ShowToast";
import DynamicTable from "../ReusableComponent/DynamicTable";
import TableSkeleton from "../ReusableComponent/TableSkeleton";
import { FiArrowLeft, FiRefreshCw } from "react-icons/fi";

const InstitutionsStudentslist = () => {
  const { token, userId } = useSelector((state) => state.auth);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const queryInstId = searchParams.get("institutionId");
  const targetInstitutionId = queryInstId || userId;

  const [loading, setLoading] = useState(false);
  const [students, setStudents] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedFilters, setSelectedFilters] = useState({});
  const [selectedRows, setSelectedRows] = useState([]);

  // Transform and Deduplicate API Data by student userId._id
  const transformStudentsData = (apiData) => {
    const uniqueMap = new Map();
    apiData.forEach((item) => {
      const uId = item.userId?._id || item._id;
      if (!uniqueMap.has(uId)) {
        uniqueMap.set(uId, item);
      }
    });

    return Array.from(uniqueMap.values()).map((item) => ({
      id: item._id,
      userid: item.userId?._id,
      batchId: item.batchId?._id,
      instituteid: item.institutionId?._id,
      name: item.userId?.name || "N/A",
      phone: item.userId?.phoneNumber || "N/A",
      state: item.userId?.state || "",
      city: item.userId?.city || "",
      examType: item.batchId?.examType?.value || "N/A",
      institution: item.institutionId?.nameofInstitution || "N/A",
      testPrice: item.testPrice ?? 0,
      paymentStatus: item.paymentStatus || "paid",
      transactionId: item.transactionId || "N/A",
      paymentMethod: item.paymentMethod || "RAZORPAY",
      date: item.autoApprovedAt
        ? new Date(item.autoApprovedAt).toLocaleDateString("en-GB")
        : item.createdAt
        ? new Date(item.createdAt).toLocaleDateString("en-GB")
        : "N/A",
    }));
  };

  // Fetch Students List (combining both Auto-Approved and Approved APIs)
  const fetchStudentsList = async () => {
    try {
      setLoading(true);
      const instId = targetInstitutionId || "695f3f832929fa82230aaf33";
      const autoApprovedUrl = `${students_list_autoapproved}&institutionId=${instId}&limit=1000`;
      const approvedUrl = `${students_list_approved}&institutionId=${instId}&limit=1000`;
      
      const [resAuto, resApproved] = await Promise.all([
        getWithTokenAPI(token, autoApprovedUrl),
        getWithTokenAPI(token, approvedUrl),
      ]);

      const autoList = Array.isArray(resAuto?.data) ? resAuto.data : [];
      const approvedList = Array.isArray(resApproved?.data) ? resApproved.data : [];

      // Combine responses from both APIs
      const combinedData = [...autoList, ...approvedList];
      const formattedData = transformStudentsData(combinedData);
      setStudents(formattedData);
    } catch (error) {
      console.error("Fetch students list error:", error);
      SafeToast.error("Failed to load students list");
      setStudents([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchStudentsList();
    }, 400);

    return () => clearTimeout(delayDebounceFn);
  }, [token, targetInstitutionId]);

  // Client-side search filtering for immediate responsive search
  const filteredStudents = useMemo(() => {
    if (!search.trim()) return students;
    const query = search.toLowerCase().trim();
    return students.filter(
      (item) =>
        item.name.toLowerCase().includes(query) ||
        item.phone.includes(query) ||
        item.city.toLowerCase().includes(query) ||
        item.examType.toLowerCase().includes(query) ||
        item.transactionId.toLowerCase().includes(query)
    );
  }, [students, search]);

  const getColumns = () => [
    {
      key: "name",
      label: "Student Name",
      render: (row) => (
        <div>
          <strong style={{ color: "#0f172a", fontSize: "14px" }}>{row.name}</strong>
        </div>
      ),
    },
    {
      key: "phone",
      label: "Phone Number",
      render: (row) => row.phone,
    },
    {
      key: "examType",
      label: "Exam Type",
      render: (row) => (
        <span
          style={{
            padding: "4px 10px",
            borderRadius: "6px",
            backgroundColor: "#eff6ff",
            color: "#2563eb",
            fontWeight: 600,
            fontSize: "12px",
          }}
        >
          {row.examType}
        </span>
      ),
    },
    {
      key: "location",
      label: "City / State",
      render: (row) => {
        const loc = [row.city, row.state].filter(Boolean).join(", ");
        return loc || "N/A";
      },
    },
    {
      key: "institution",
      label: "Institution",
      render: (row) => row.institution,
    },
    // {
    //   key: "price",
    //   label: "Price",
    //   render: (row) => <strong>₹{row.testPrice}</strong>,
    // },
    // {
    //   key: "paymentStatus",
    //   label: "Payment Status",
    //   render: (row) => (
    //     <span
    //       style={{
    //         padding: "4px 10px",
    //         borderRadius: "16px",
    //         backgroundColor: row.paymentStatus === "paid" ? "#dcfce7" : "#fef3c7",
    //         color: row.paymentStatus === "paid" ? "#15803d" : "#b45309",
    //         fontWeight: 600,
    //         fontSize: "12px",
    //         textTransform: "capitalize",
    //       }}
    //     >
    //       ● {row.paymentStatus}
    //     </span>
    //   ),
    // },
    // {
    //   key: "transactionId",
    //   label: "Transaction ID",
    //   render: (row) => (
    //     <span
    //       style={{
    //         fontFamily: "monospace",
    //         fontSize: "12px",
    //         backgroundColor: "#f1f5f9",
    //         padding: "4px 8px",
    //         borderRadius: "4px",
    //         border: "1px dashed #cbd5e1",
    //       }}
    //     >
    //       {row.transactionId}
    //     </span>
    //   ),
    // },
    {
      key: "date",
      label: "Approval Date",
      render: (row) => row.date,
    },
  ];

  const handleFilterChange = (key, option) => {
    setSelectedFilters((prev) => ({
      ...prev,
      [key]: option,
    }));
  };

  return (
    <div className="batch-list-page" style={{ padding: "24px" }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "20px",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <button
            onClick={() =>
              navigate(
                queryInstId
                  ? `/institution/dashboard?institutionId=${queryInstId}`
                  : -1
              )
            }
            style={{
              border: "1px solid #cbd5e1",
              background: "#ffffff",
              borderRadius: "8px",
              padding: "8px 12px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              gap: "6px",
              color: "#475569",
              fontWeight: 500,
            }}
          >
            <FiArrowLeft size={18} /> Back
          </button>
          <h2 style={{ margin: 0, fontSize: "22px", color: "#0f172a" }}>
            Total Students 
          </h2>
        </div>

     
      </div>

      {loading ? (
        <TableSkeleton columns={getColumns()} rows={6} hasActions={false} />
      ) : (
        <DynamicTable
          columns={getColumns()}
          data={filteredStudents}
          filters={[]}
          search={search}
          setSearch={setSearch}
          selectedFilters={selectedFilters}
          handleFilterChange={handleFilterChange}
          selectedRows={selectedRows}
          setSelectedRows={setSelectedRows}
          onSearchClick={fetchStudentsList}
          activeTab="All"
        />
      )}
    </div>
  );
};

export default InstitutionsStudentslist;